import {
  CLIENT_FIELD,
  type ClientOrigin,
  type FamilyKeyOf,
  type FixedSubject,
  type RecordFamilyKey,
  type RecordFamilyKind,
  type RecordFamilyTraits,
  type RecordTraits,
  type RecordSigners,
  type RecordSourceLink,
  type RecordSourceOrigin,
  type RecordSubjectKind,
} from "@/features/forms/shared/record/record-family";
import { PREVIEW_STEP } from "@/features/forms/shared/record/record-route";
import {
  ACCOUNT_OPENING_CORPORATION_ENTITY,
  ACCOUNT_OPENING_INDIVIDUAL_ENTITY,
  ADVICE_CORPORATION_ENTITY,
  ADVICE_INDIVIDUAL_ENTITY,
  ENDOWMENT_INSURANCE_APPLICATION_CORPORATION_ENTITY,
  ENDOWMENT_INSURANCE_APPLICATION_ENTITY,
  ORDER_INSTRUCTION_ENTITY,
} from "@/shared/events/entity-invalidation";

const FORM_TYPE_KEY: Record<RecordFamilyKind, string> = {
  advice: "advisory.formType.investmentAdvice",
  account_opening: "advisory.formType.accountOpening",
  order_instruction: "advisory.formType.orderInstruction",
  endowment_insurance: "advisory.formType.endowmentInsurance",
  sign_document: "advice.formType.signDocument",
};

export const SIGN_DOCUMENT_ENTITY = "sign_document";

export const RECORD_FAMILY_TRAITS = {
  "advice-individual": {
    ...recordTraits("advice-individual", "advice", ADVICE_INDIVIDUAL_ENTITY),
    ...fixedSubject("individual"),
    ...clientOrigin("individual"),
  },
  "account-opening-individual": {
    ...recordTraits(
      "account-opening-individual",
      "account_opening",
      ACCOUNT_OPENING_INDIVIDUAL_ENTITY,
    ),
    ...fixedSubject("individual"),
    ...clientOrigin("individual"),
  },
  "advice-corporation": {
    ...recordTraits("advice-corporation", "advice", ADVICE_CORPORATION_ENTITY),
    ...fixedSubject("corporation"),
    ...clientOrigin("corporation"),
  },
  "account-opening-corporation": {
    ...recordTraits(
      "account-opening-corporation",
      "account_opening",
      ACCOUNT_OPENING_CORPORATION_ENTITY,
    ),
    ...fixedSubject("corporation"),
    ...clientOrigin("corporation"),
  },
  "order-instruction": {
    ...recordTraits("order-instruction", "order_instruction", ORDER_INSTRUCTION_ENTITY),
    createdStep: PREVIEW_STEP,
    signedByFirm: false,
    subject: "per-record",
    origin: sourceOrigin([
      {
        family: "advice-individual",
        idField: "advice_individual_v1_id",
        available: "available-advice-individual",
        quantBound: true,
      },
      {
        family: "advice-corporation",
        idField: "advice_corporation_v1_id",
        available: "available-advice-corporation",
        quantBound: true,
      },
    ]),
  },
  "endowment-insurance-individual": {
    ...recordTraits(
      "endowment-insurance-individual",
      "endowment_insurance",
      ENDOWMENT_INSURANCE_APPLICATION_ENTITY,
      "v1/endowment-insurance-application",
      {
        source: "field",
        role: "policyholder",
        email: "policyholder.email",
        first: "policyholder.first_name",
        last: "policyholder.last_name",
      },
    ),
    ...fixedSubject("individual"),
    origin: sourceOrigin([
      {
        family: "account-opening-individual",
        idField: "account_opening_individual_v1_id",
        available: "available-account-openings",
      },
    ]),
  },
  "endowment-insurance-corporation": {
    ...recordTraits(
      "endowment-insurance-corporation",
      "endowment_insurance",
      ENDOWMENT_INSURANCE_APPLICATION_CORPORATION_ENTITY,
      "v1/endowment-insurance-application-corporation",
      {
        source: "table",
        role: "signatory",
        path: "policyholder.signatories",
        first: "first_name",
        last: "last_name",
        email: "email",
      },
    ),
    ...fixedSubject("corporation"),
    origin: sourceOrigin([
      {
        family: "account-opening-corporation",
        idField: "account_opening_corporation_v1_id",
        available: "available-account-openings",
      },
    ]),
  },
  "sign-document": {
    ...recordTraits("sign-document", "sign_document", SIGN_DOCUMENT_ENTITY, "v1/sign-document", {
      source: "table",
      role: "signatory",
      path: "additional_signers",
      first: "first_name",
      last: "last_name",
      email: "email",
    }),
    signedByFirm: false,
    subject: "per-record",
    origin: { from: "client" },
  },
} satisfies Record<RecordFamilyKey, RecordFamilyTraits>;

function recordTraits<K extends RecordFamilyKind>(
  key: FamilyKeyOf<K>,
  kind: K,
  entity: string,
  path = `v1/${key}`,
  signers: RecordSigners = { source: "client-contact" },
): RecordTraits<K> {
  const advice = kind === "advice";
  return {
    key,
    kind,
    path,
    entity,
    signers,
    signedByFirm: true,
    carriesPortfolio: advice,
    offersDeclarationDraft: advice || kind === "account_opening",
    formTypeKey: FORM_TYPE_KEY[kind],
  };
}

function fixedSubject<S extends RecordSubjectKind>(subject: S): FixedSubject & { subject: S } {
  return {
    subject,
    clientField: CLIENT_FIELD[subject],
  };
}

function clientOrigin(subject: RecordSubjectKind): ClientOrigin {
  return {
    origin: { from: "client" },
    identificationNumberField: `${subject}_identification_number_id`,
    addressField: `${subject}_address_id`,
  };
}

function sourceOrigin(sources: readonly RecordSourceLink[]): RecordSourceOrigin {
  return { from: "source", sources };
}
